<template>
    <v-select
        ref="select"
        :placeholder="placeholder"
        :options="options"
        :filterable="false"
        :noDrop="isDisableDrop"
        :disabled="isDisabled"
        @search="fetchPatients"
        @input="submit"
        @close="onClose"
        class="search-patient show-actions no-border-left responsive-form"
        v-model="currentPatient"
    >
        <template v-slot:option="{ label }">
            <b>{{ label }}</b>
        </template>

        <template v-slot:no-options="{ search }">
            <div class="no-result">
                <span v-if="search === ''">
                    Type to search
                </span>
                <span v-else-if="isLoading">
                    Loading...
                </span>
                <span v-else>
                    {{ emptyOptionsMessage }}
                </span>
            </div>
        </template>

        <template v-slot:open-indicator>
            <!-- this empty div is intentional, do not remove. this is for a "replacement" to default arrow -->
            <div></div>
        </template>

        <template v-slot:spinner>
            <img
                v-if="isLoading"
                class="spinner"
                src="/apps/assets/img/ajax-spinner.gif"
            />
        </template>

        <template v-slot:list-footer>
            <li ref="footerLoader" class="footerloader" v-if="hasNextPage">
                Loading...
            </li>
        </template>
    </v-select>
</template>

<script>
import vSelect from "vue-select";
import { debounce } from "lodash";

const PATIENT_SEARCH_URI = `${process.env.BARRAMUNDI}patient/search-patient`;

export default {
    components: {
        vSelect
    },
    props: {
        placeholder: {
            type: String
        },
        emptyOptionsMessage: {
            type: String,
            default: "No matches found"
        },
        isDisabled: {
            type: Boolean,
            default: false
        },
        focusOnload: {
            default: false,
            type: Boolean
        }
    },
    data() {
        return {
            query: "",
            isDisableDrop: true,
            options: [],
            isLoading: false,
            resultMeta: null,
            currentPatient: ""
        };
    },
    watch: {
        options(options = []) {
            let ul = document.querySelector(".search-patient ul");

            if (options.length === 0) {
                ul.style.width = "";
                ul.style.left = "";
                ul.style.right = "";
                ul.style.borderTopLeftRadius = "";
                ul.style.borderTopRightRadius = "";
            } else {
                ul.style.width = "unset";
                ul.style.left = "unset";
                ul.style.right = "0";
                ul.style.borderTopLeftRadius = "5px";
                ul.style.borderTopRightRadius = "5px";
            }
        }
    },
    mounted() {
        this.observer = new IntersectionObserver(this.infiniteScroll);

        if (this.focusOnload) {
            this.focus();
        }
    },
    computed: {
        filterType() {
            return this.$store.state.patient.filterType;
        }
    },
    methods: {
        focus() {
            this.$refs.select.$el.querySelector("input").focus();
        },
        submit(value) {
            this.$emit("select", value);
            this.query = "";
            this.options = [];
            this.currentPatient = "";
        },
        fetchPatients(search) {
            this.query = search;
            this.isLoading = search !== "" ? true : false;
            this.searchPatients(search, this);
        },
        searchPatients: debounce(async (search, self) => {
            // no more minimum 3 chars
            if (search === "") {
                return;
            }

            const res = await self.$http.get(PATIENT_SEARCH_URI, {
                params: {
                    query: search,
                    all_status: true,
                    filter_type: self.filterType
                }
            });

            const { message, resources, meta } = res.data.response;

            if (message.code === 200) {
                self.options = self.generatePatientList(resources);
                self.isDisableDrop = false;
                self.isLoading = false;
                self.resultMeta = {
                    total: meta.total,
                    currentPage: 1,
                    lastPage: Math.ceil(meta.total / 20) // total divide by per page, then ceil
                };

                if (self.options.length < meta.total) {
                    self.hasNextPage = true;
                } else {
                    self.hasNextPage = false;
                }
            } else {
                self.options = [];
                self.isLoading = false;
            }

            await self.registerFootLoader();
        }, 350),

        generatePatientList(records = []) {
            let result = [];

            for (let record of records) {
                result.push({
                    label: `${record.fullname} (${record.index})`,
                    id: record.id,
                    actorId: record.actor_id,
                    value: record.id
                });
            }

            return result;
        },
        async registerFootLoader() {
            if (this.hasNextPage) {
                await this.$nextTick();
                this.observer.observe(this.$refs.footerLoader);
            }
        },
        onClose() {
            this.observer.disconnect();
            this.options = []; // clear
            this.hasNextPage = false;
            this.currentPatient = "";
        },
        async infiniteScroll([{ isIntersecting, target }]) {
            const self = this;

            // references: https://vue-select.org/guide/infinite-scroll.html
            if (isIntersecting && this.hasNextPage) {
                const ul = target.offsetParent;
                const scrollTop = target.offsetParent.scrollTop;
                await this.$nextTick();

                this.fetchNextPage(async () => {
                    await self.$nextTick();
                    ul.scrollTop = scrollTop; // keep scroll position after we append the options
                });
            }
        },
        async fetchNextPage(callback) {
            if (!this.resultMeta) {
                return;
            }

            const { currentPage, lastPage } = this.resultMeta;
            let nextPage = currentPage + 1;

            if (currentPage >= lastPage) {
                this.hasNextPage = false;
                return;
            }

            const res = await this.$http.get(PATIENT_SEARCH_URI, {
                params: {
                    page: nextPage,
                    query: this.query,
                    all_status: true,
                    filter_type: this.filterType
                }
            });

            const { message, meta, resources } = res.data.response;

            if (message.code === 200) {
                this.resultMeta.currentPage = nextPage;
                this.options = [
                    ...this.options,
                    ...this.generatePatientList(resources)
                ];

                if (this.options.length < meta.total) {
                    this.hasNextPage = true;
                } else {
                    this.hasNextPage = false;
                }

                if (callback) {
                    callback();
                }
            } else {
                this.hasNextPage = false;
            }
        }
    }
};
</script>

<style scoped>
.no-result {
    margin-left: 10px;
}
.footerloader {
    padding: 3px 20px;
    font-style: italic;
}
</style>

<style>
/* 
VET-564 : add dropdown menu on patient search dashboard 
border left none to combine with dropdown menu
*/
.no-border-left {
    min-width: 180px !important;
}

@media only screen and (min-width: 600px) {
    /* For tablets: */
    .no-border-left {
        min-width: 224px !important;
    }
}

.no-border-left .vs__dropdown-toggle {
    border-top-left-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
    box-shadow: inset 0 1px 0 hsla(0, 0%, 100%, 0.15),
        0 1px 1px rgba(0, 0, 0, 0.075);
}
</style>
